import React, { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Clock,
  Award,
  Shield,
  RefreshCw,
  Mail,
  Archive,
  Eye,
  Trash2,
  Play,
  PlayCircle,
  X,
  CheckCircle2,
  XCircle,
  CircleDot,
  Wrench,
  Loader2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
} from "lucide-react";

/**
 * v1.2 PR-B.2 — Super-admin Maintenance cron control panel.
 *
 * Backed by:
 *   GET  /api/super-admin/maintenance/crons
 *   GET  /api/super-admin/maintenance/crons/{key}/history?limit=20
 *   POST /api/super-admin/maintenance/crons/{key}/run
 *   POST /api/super-admin/maintenance/crons/run-all  (body: {confirm:true})
 *
 * No global pause/kill switch in this PR (deferred to v2).
 */
const ICONS = { Clock, Award, Shield, RefreshCw, Mail, Archive, Eye, Trash2 };

export default function SuperAdminMaintenance() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({});            // {cron_key: true}
  const [runAllOpen, setRunAllOpen] = useState(false);
  const [runAllBusy, setRunAllBusy] = useState(false);
  const [drawer, setDrawer] = useState(null);            // selected cron row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/maintenance/crons");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (key) => {
    setRunning((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.post(`/super-admin/maintenance/crons/${key}/run`);
      toast.success(`${key} ran in ${data.duration_ms ?? "?"} ms`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning((s) => { const { [key]: _, ...rest } = s; return rest; });
      load();
    }
  };

  const runAll = async () => {
    setRunAllBusy(true);
    try {
      const { data } = await api.post(
        "/super-admin/maintenance/crons/run-all",
        { confirm: true },
        { timeout: 120_000 },
      );
      const failed = data.filter((r) => r.status === "failed").length;
      if (failed === 0) toast.success(`All ${data.length} crons completed`);
      else toast.warning(`${data.length - failed}/${data.length} succeeded · ${failed} failed`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunAllBusy(false);
      setRunAllOpen(false);
      load();
    }
  };

  return (
    <SuperAdminLayout>
      <div data-testid="super-admin-maintenance-page" className="space-y-6">
        <header className="flex items-end justify-between gap-4">
          <div>
            <div className="eb-overline mb-1">Super admin</div>
            <h1 className="text-3xl font-semibold text-navy-900 inline-flex items-center gap-2">
              <Wrench className="w-7 h-7 text-orange-500" /> Maintenance
            </h1>
            <p className="text-sm text-navy-500 mt-1 max-w-3xl">
              Every cron in the platform is listed below with its last-run telemetry.
              Run any cron manually with one click — every manual run is audit-logged.
            </p>
          </div>
          <button
            onClick={() => setRunAllOpen(true)}
            disabled={runAllBusy || Object.keys(running).length > 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
            data-testid="maintenance-run-all"
          >
            {runAllBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running all…</> : <><PlayCircle className="w-3.5 h-3.5" /> Run all</>}
          </button>
        </header>

        {/* v2 PR-1 Feature 3 — SLO cards above the schedule table. */}
        <SloSection rows={rows} onCardClick={(row) => setDrawer(row)} />

        <div className="bg-white border border-navy-100 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="maintenance-table">
              <thead className="bg-navy-50/60 text-xs text-navy-600 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2 w-[34%]">Cron</th>
                  <th className="text-left px-4 py-2">Last run</th>
                  <th className="text-left px-4 py-2">Duration</th>
                  <th className="text-left px-4 py-2 w-[22%]">Metrics</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {loading && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-navy-500">Loading…</td></tr>
                )}
                {!loading && rows.map((r) => {
                  const Icon = ICONS[r.icon] || Clock;
                  const isRunning = !!running[r.key];
                  return (
                    <tr key={r.key}
                        onClick={() => setDrawer(r)}
                        className="hover:bg-navy-50/40 cursor-pointer transition-colors"
                        data-testid={`maintenance-row-${r.key}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <Icon className="w-4 h-4 mt-0.5 text-orange-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-navy-900">{r.name}</div>
                            <div className="text-xs text-navy-500 mt-0.5 truncate max-w-md">
                              {r.description}
                            </div>
                            <code className="text-[10px] text-navy-400 font-mono mt-0.5 block">
                              {r.endpoint}
                            </code>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <LastRunCell row={r.last_run} recentFailures={r.recent_failures_7d} />
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-navy-700 font-mono">
                        {r.last_run?.duration_ms != null ? `${r.last_run.duration_ms} ms` : <span className="text-navy-300">—</span>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <MetricsSummary metrics={r.last_run?.metrics} />
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); runNow(r.key); }}
                          disabled={isRunning || runAllBusy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-navy-900 text-white hover:bg-navy-800 disabled:opacity-50"
                          data-testid={`run-now-${r.key}`}
                        >
                          {isRunning ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</>
                                     : <><Play className="w-3.5 h-3.5" /> Run now</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Drawer cron={drawer} onClose={() => setDrawer(null)} />
      <RunAllConfirm open={runAllOpen} onCancel={() => setRunAllOpen(false)} onConfirm={runAll} busy={runAllBusy} />
    </SuperAdminLayout>
  );
}

function statusPill(status) {
  if (status === "success") return { cls: "bg-success/10 text-success border-success/30", Icon: CheckCircle2, label: "Success" };
  if (status === "failed")  return { cls: "bg-danger/10 text-danger border-danger/30",   Icon: XCircle,        label: "Failed" };
  if (status === "running") return { cls: "bg-warning/10 text-warning border-warning/30", Icon: Loader2,        label: "Running" };
  return                            { cls: "bg-navy-50 text-navy-500 border-navy-200",   Icon: CircleDot,      label: "Never run" };
}

function _relativeTime(iso) {
  if (!iso) return "—";
  try {
    const delta = (Date.now() - new Date(iso).getTime()) / 1000;
    if (delta < 60) return `${Math.round(delta)}s ago`;
    if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
    if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
    return `${Math.round(delta / 86400)}d ago`;
  } catch { return iso; }
}

function LastRunCell({ row, recentFailures }) {
  const pill = statusPill(row?.status);
  return (
    <div className="space-y-1">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${pill.cls}`}>
        <pill.Icon className={`w-3 h-3 ${row?.status === "running" ? "animate-spin" : ""}`} /> {pill.label}
        {row?.triggered_by && <span className="ml-1 opacity-70">· {row.triggered_by}</span>}
      </span>
      <div className="text-xs text-navy-500 font-mono">
        {row?.started_at ? `${_relativeTime(row.started_at)} · ${new Date(row.started_at).toLocaleString()}` : "—"}
      </div>
      {recentFailures > 0 && (
        <div className="inline-flex items-center gap-1 text-[10px] text-danger font-medium">
          <AlertTriangle className="w-3 h-3" /> {recentFailures} failure(s) in last 7d
        </div>
      )}
    </div>
  );
}

function MetricsSummary({ metrics }) {
  if (!metrics || Object.keys(metrics).length === 0) {
    return <span className="text-xs text-navy-300">—</span>;
  }
  return (
    <div className="text-xs font-mono space-y-0.5 text-navy-700">
      {Object.entries(metrics).slice(0, 4).map(([k, v]) => (
        <div key={k} className="truncate max-w-[180px]">
          <span className="text-navy-500">{k}:</span> <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
    </div>
  );
}

function Drawer({ cron, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [innerRun, setInnerRun] = useState(null);

  useEffect(() => {
    if (!cron) { setHistory([]); setInnerRun(null); return; }
    setLoading(true);
    api.get(`/super-admin/maintenance/crons/${cron.key}/history?limit=20`)
      .then(({ data }) => setHistory(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cron]);

  if (!cron) return null;
  const Icon = ICONS[cron.icon] || Clock;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="maintenance-drawer">
      <button onClick={onClose} className="flex-1 bg-black/40" aria-label="Close drawer" />
      <aside className="w-full md:w-[640px] bg-white shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-white px-5 py-4 border-b border-navy-100 flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <Icon className="w-5 h-5 text-orange-500" />
            <div>
              <div className="eb-overline">Cron</div>
              <h2 className="text-sm font-semibold text-navy-900">{cron.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-navy-400 hover:text-navy-700" data-testid="maintenance-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="px-5 py-4 space-y-4 text-sm">
          <p className="text-navy-700 leading-relaxed">{cron.description}</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="border border-navy-100 rounded p-2">
              <div className="eb-overline">Endpoint</div>
              <code className="text-navy-800 font-mono break-all">{cron.endpoint}</code>
            </div>
            <div className="border border-navy-100 rounded p-2">
              <div className="eb-overline">Cron key</div>
              <code className="text-navy-800 font-mono">{cron.key}</code>
            </div>
          </div>

          <div>
            <div className="eb-overline mb-2 mt-3">Run history (last 20)</div>
            {loading && <div className="text-xs text-navy-500">Loading…</div>}
            {!loading && history.length === 0 && (
              <div className="text-xs text-navy-400">No runs yet.</div>
            )}
            {!loading && (
              <ul className="divide-y divide-navy-100 border border-navy-100 rounded" data-testid="maintenance-history">
                {history.map((h) => {
                  const p = statusPill(h.status);
                  return (
                    <li key={h.id}
                        onClick={() => setInnerRun(h)}
                        className="px-3 py-2 hover:bg-navy-50/40 cursor-pointer flex items-start justify-between gap-3"
                        data-testid={`maintenance-history-row`}>
                      <div className="min-w-0">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${p.cls}`}>
                          <p.Icon className="w-2.5 h-2.5" /> {p.label}
                        </span>
                        <span className="text-[10px] text-navy-500 ml-2">{h.triggered_by}</span>
                        <div className="text-xs text-navy-700 font-mono mt-0.5">
                          {h.started_at ? new Date(h.started_at).toLocaleString() : "—"}
                        </div>
                      </div>
                      <div className="text-xs text-navy-500 font-mono whitespace-nowrap">
                        {h.duration_ms != null ? `${h.duration_ms} ms` : "—"}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {innerRun && (
            <div className="border border-navy-200 rounded p-3 bg-navy-50/50" data-testid="maintenance-inner-run">
              <div className="flex items-center justify-between mb-2">
                <div className="eb-overline">Run detail · {new Date(innerRun.started_at).toLocaleString()}</div>
                <button onClick={() => setInnerRun(null)} className="text-navy-400 hover:text-navy-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs">
                <div className="mb-2">
                  <span className="text-navy-500">Status:</span>{" "}
                  <span className="font-medium">{innerRun.status}</span>
                </div>
                <div className="mb-2">
                  <span className="text-navy-500">Duration:</span>{" "}
                  <code className="font-mono">{innerRun.duration_ms} ms</code>
                </div>
                <div className="mb-2 eb-overline">Metrics</div>
                <pre className="bg-white border border-navy-100 rounded p-2 text-[10px] text-navy-800 overflow-x-auto">
                  {JSON.stringify(innerRun.metrics || {}, null, 2)}
                </pre>
                {innerRun.error && (
                  <>
                    <div className="mt-2 mb-1 eb-overline text-danger">Error trace</div>
                    <pre className="bg-danger/5 border border-danger/30 rounded p-2 text-[10px] text-danger overflow-x-auto whitespace-pre-wrap">
                      {innerRun.error}
                    </pre>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function RunAllConfirm({ open, onCancel, onConfirm, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="run-all-confirm">
      <div className="bg-white border border-navy-100 rounded-xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-warning" />
          <h2 className="text-base font-semibold text-navy-900">Run all 8 crons sequentially?</h2>
        </div>
        <p className="text-sm text-navy-700 mb-4 leading-relaxed">
          This will trigger every cron one after the other — up to several minutes total — and
          will have <strong>real side effects</strong>: emails sent (insurance alerts, weekly digest),
          retention sweep redactions, watchlist expirations, and pre-registration occurrence
          materialisations.
        </p>
        <p className="text-xs text-navy-500 mb-4">
          Rate-limited to once every 5 minutes per user.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-md border border-navy-200 hover:bg-navy-50 disabled:opacity-50"
                  data-testid="run-all-cancel">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-danger text-white hover:bg-danger/90 disabled:opacity-50"
                  data-testid="run-all-confirm-btn">
            {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</> : <><PlayCircle className="w-3.5 h-3.5" /> Run all now</>}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// v2 PR-1 Feature 3 — Cron SLO cards (success rate, latency, alerts) for the
// last 30 days. Sits ABOVE the existing schedule table; clicking a card opens
// the same per-cron history drawer.
// ============================================================================

const ALERT_LABEL = {
  success_rate_below_threshold: "Below SLA",
  p95_spike: "p95 spike",
  low_volume: "Low volume",
};

function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function TrendIcon({ trend }) {
  if (trend === "improving") return <TrendingUp className="w-3 h-3 text-success-600" />;
  if (trend === "declining") return <TrendingDown className="w-3 h-3 text-danger-600" />;
  return <Minus className="w-3 h-3 text-navy-400" />;
}

function SloCard({ row, slo, onClick }) {
  const sr = slo.success_rate_30d;
  const srStr = sr == null ? "—" : `${sr.toFixed(1)}%`;
  // Bar shows fill ratio; fully red when below the SLO floor (95%).
  const barFill = sr == null ? 0 : Math.max(0, Math.min(100, sr));
  const below = sr != null && sr < 95;
  const barColour = below
    ? "bg-danger-500"
    : sr != null && sr < 99
    ? "bg-warning-500"
    : "bg-success-500";
  const Icon = ICONS[row?.icon] || Activity;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-navy-100 rounded-xl p-4 hover:border-orange-300 transition-colors shadow-sm"
      data-testid={`slo-card-${row?.key || "unknown"}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 truncate">
          <Icon className="w-3.5 h-3.5 text-orange-500" /> {row?.name || row?.key}
        </div>
        <TrendIcon trend={slo.trend_7d_vs_30d} />
      </div>
      <div className="text-2xl font-bold text-navy-900 leading-tight" data-testid={`slo-card-rate-${row?.key}`}>
        {srStr}
      </div>
      <div className="h-1.5 w-full rounded-full bg-navy-100 overflow-hidden mt-1.5 mb-2">
        <div className={`h-full ${barColour}`} style={{ width: `${barFill}%` }} />
      </div>
      <div className="text-[11px] text-navy-500 leading-snug">
        p50 {fmtMs(slo.p50_duration_ms_30d)} · p95 {fmtMs(slo.p95_duration_ms_30d)} · n={slo.total_runs_30d}
      </div>
      {slo.alerts?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {slo.alerts.map((a) => (
            <span
              key={a}
              className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning-50 text-warning-700 border border-warning-200"
              data-testid={`slo-card-alert-${row?.key}-${a}`}
            >
              <AlertTriangle className="w-2.5 h-2.5" /> {ALERT_LABEL[a] || a}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function SloSection({ rows, onCardClick }) {
  const [slo, setSlo] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/super-admin/maintenance/crons/slo");
        if (alive) { setSlo(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  if (err) {
    return (
      <section
        className="bg-white border border-danger/30 rounded-xl p-4 text-sm text-danger"
        data-testid="slo-section-error"
      >
        Could not load SLOs: {err}
      </section>
    );
  }
  return (
    <section data-testid="slo-section" className="space-y-3">
      <header className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Activity className="w-4 h-4 text-orange-500" />
          <h2 className="font-semibold text-navy-900">Service-level objectives</h2>
          <span className="text-xs text-navy-500">(last 30 days · target 95% success)</span>
        </div>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {(rows || []).map((row) => {
          const s = slo?.[row.key];
          if (!s) {
            return (
              <div key={row.key} className="bg-white border border-navy-100 rounded-xl p-4">
                <div className="text-xs text-navy-500">{row.name || row.key}</div>
                <div className="text-2xl font-bold text-navy-300">—</div>
                <div className="text-[11px] text-navy-400">no data yet</div>
              </div>
            );
          }
          return <SloCard key={row.key} row={row} slo={s} onClick={() => onCardClick && onCardClick(row)} />;
        })}
      </div>
    </section>
  );
}
